/**
 * VideoEditorNode (Canvas V2)
 *
 * Configuration node for stitching video clips and audio in the Timeline Editor.
 * Rendered artifacts are created as separate Video Upload nodes.
 */

import { ydocSafeCloneNodeData } from '../../../../utils/ydocNodeData'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ASPECT_RATIO_MAP, ASPECT_WIDTH_MAP, NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { Handle, Position, type NodeProps, useNodes, useReactFlow } from 'reactflow'
import { Maximize2, Minimize2, Pause, Play, Scissors, Volume2, VolumeX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cancelNodeExecution, executeNode, getDreamVibeCapabilities, writeVideoEditorEditSpec } from '../../../../api/client'
import {
  AddToChatIcon,
  AgentNodeToolbar,
  useNodeActions,
  useUpscaleVideoAction,
  type ToolbarAction,
} from '../shared'
import { videoDimensionsFromMetadata } from '../shared/useUpscaleVideoAction'
import { VideoReferencePicker } from '../VideoGeneratorNode/VideoReferencePicker'
import { ReferenceSelector, type SelectedReference } from '../shared/ReferenceSelector'
import { buildOrderedReferenceMedia, replaceReferenceMediaEdges, referenceMediaKey, isReferenceMediaEdge, getAudioAssetPath, getAudioReferenceLabel } from '../shared/mentionUtils'
import {
  getNodeVideoOutput,
  resolvePickedVideoAsset,
} from '../shared/pickedVideoAsset'
import { TimelineEditorModal } from '../../panels/timeline/TimelineEditorModal'
import { TimelinePreviewPlayer, type TimelinePreviewControls } from '../../panels/timeline/TimelinePreviewPlayer'
import { normalizeTimelineRenderConfig, type TimelineRenderConfig } from '../../panels/timeline/types'
import {
  coerceTimelineDurationHint,
  isSymbolicTimelineSourceRef,
  isVideoExportDurationTooShort,
  timelineRenderConfigDuration,
} from '../../panels/timeline/timelineUtils'
import { appendEditSpecMedia } from '../../panels/timeline/importEditSpecMedia'
import { dreamwriteEditSpecDurationSeconds, fingerprintDreamwriteEditSpec } from '../../panels/timeline/dreamvibeAdapter'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { getTypedNodeData, type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import { dataStatus, dataStrOpt, dataValue, type ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { ExportConfirmSheet, buildDefaultExportName } from './ExportConfirmSheet'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'
import {
  audioKindForLegacyRole,
  isLegacyTimelineAudioRole,
  type TimelineAudioKind,
} from '../../panels/timeline/timelineAudioKind'
import { trackEditorOpened, trackGenerationCancelled } from '../../../../analytics/interactionAnalytics'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

// Match the standard landscape VideoGenerator card, including its preview ratio.
const NODE_WIDTH = ASPECT_WIDTH_MAP['16:9']
type VideoEditorNodeData = ReactFlowCanvasNodeDataByType['videoEditor']

const referenceMediaOrderLog = createNadDebug('dw:reference-media-order')
const chromeLog = createNadDebug('dw:videoeditor-canvas-preview:chrome')

const renderLog = createNadDebug('dw:video-editor:canvas-render')

const exportFlowLog = createNadDebug('dw:video-editor:export-flow')
const exportStateLog = createNadDebug('dw:video-editor-export-state:presentation')
const exportFallbackLog = createNadDebug('dw:video-editor-audio-preview-and-export-button')
const timelineQaLog = createNadDebug('dw:video-editor:timeline-qa')
const renderLockLog = createNadDebug('dw:video-editor:render-lock')
const pendingSourcesLog = createNadDebug('dw:video-editor:pending-sources')
const publishedHydrationLog = createNadDebug(
  'dw:published-video-editor-hydration:video-editor',
)

const STATUS_CLASS: Record<string, { dot: string; text: string }> = {
  idle: { dot: 'bg-text-muted', text: 'Ready' },
  pending_execution: { dot: 'bg-warning animate-pulse', text: 'Waiting' },
  pending_cost_confirmation: { dot: 'bg-warning animate-pulse', text: 'Confirm cost' },
  running: { dot: 'bg-warning animate-pulse', text: 'Rendering...' },
  completed: { dot: 'bg-success', text: 'Done' },
  error: { dot: 'bg-destructive', text: 'Error' },
}

const STOPPED_RENDER_RE = /\b(cancelled|canceled|cancelled by user|canceled by user|user cancelled|user canceled|stopped by user|user stopped|render stopped by user)\b/i

function isStoppedRender(status: string, error?: string | null, progressMessage?: string | null): boolean {
  if (status !== 'error') return false
  return STOPPED_RENDER_RE.test(error ?? '') || STOPPED_RENDER_RE.test(progressMessage ?? '')
}

function isVideoEditorRenderBusy(status: string): boolean {
  return status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation'
}

function getStatusConfig(status: string, error?: string | null, progressMessage?: string | null) {
  if (isStoppedRender(status, error, progressMessage)) {
    return { dot: 'bg-warning', text: 'Stopped' }
  }
  return STATUS_CLASS[status] || STATUS_CLASS.idle
}

function referenceKey(ref: Pick<SelectedReference, 'nodeId' | 'mediaType' | 'sourceHandle' | 'fromIndex'>): string {
  return `${ref.nodeId}:${ref.sourceHandle ?? ref.mediaType}:${ref.fromIndex ?? ''}`
}

function dedupeReferences(refs: SelectedReference[]): SelectedReference[] {
  const seenKeys = new Set<string>()
  const deduped: SelectedReference[] = []

  for (const ref of refs) {
    const key = referenceKey(ref)
    if (seenKeys.has(key)) continue
    seenKeys.add(key)
    deduped.push(ref)
  }

  return deduped
}

type ClipReference = { id: string; path: string; name: string; duration?: number }
type AudioClipReference = ClipReference & { audioKind?: TimelineAudioKind }

function timelineClipReferenceName(path: string, fallback: string): string {
  return path.split('/').pop() || fallback
}

function savedTimelineVideoReferences(
  timelineConfig: TimelineRenderConfig | undefined,
  fallbackName: string,
): ClipReference[] {
  const refs: ClipReference[] = []
  for (const clip of timelineConfig?.timeline?.clips ?? []) {
    if (!clip.source_path || isSymbolicTimelineSourceRef(clip.source_path)) continue
    const duration =
      coerceTimelineDurationHint(clip.original_duration) ??
      coerceTimelineDurationHint(
        typeof clip.start_time === 'number' && typeof clip.end_time === 'number'
          ? Math.max(0, clip.end_time - clip.start_time)
          : undefined,
      )
    refs.push({
      id: clip.id ?? clip.source_node_id ?? clip.source_path,
      path: clip.source_path,
      name: timelineClipReferenceName(clip.source_path, fallbackName),
      duration,
    })
  }
  return refs
}

function getAudioKind(node: { type?: string | null; data?: object } | undefined): TimelineAudioKind {
  const soundExpertData = node ? getTypedNodeData({ type: node.type ?? '', data: node.data }, 'soundExpert') : undefined
  const mode = soundExpertData?.in_mode
  if (mode === 'music' || mode === 'sfx' || mode === 'speech') return mode
  const legacyRole = dataStrOpt(node?.data, 'role')
  if (isLegacyTimelineAudioRole(legacyRole)) {
    return audioKindForLegacyRole(legacyRole) ?? 'audio'
  }
  return 'audio'
}

function EditorActions({
  renderBusy, renderDisabled, hasClips, isCancelling, exportBlockReason,
  openEditorDisabled, openEditorDisabledReason,
  onOpenEditor, onRender, onCancelRender,
}: {
  renderBusy: boolean
  renderDisabled: boolean
  hasClips: boolean
  isCancelling: boolean
  exportBlockReason?: string
  openEditorDisabled: boolean
  openEditorDisabledReason?: string
  onOpenEditor: () => void
  onRender: () => void
  onCancelRender: () => void
}) {
  const { t } = useTranslation('canvas')
  return (
    <div
      className="flex gap-2 border-t border-border p-3 nodrag nopan"
      data-testid="video-editor-actions"
      data-nux-anchor="video-editor-controls"
    >
      <button type="button" onClick={onOpenEditor} disabled={openEditorDisabled}
        title={openEditorDisabledReason}
        className="h-9 flex-1 rounded-lg border border-input-border bg-surface-1 text-sm font-medium text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
        data-testid="video-editor-open-editor">
        {t('videoEditorNode.openEditor')}
      </button>
      <button type="button" onClick={renderBusy ? onCancelRender : onRender}
        disabled={renderBusy ? isCancelling : !hasClips || renderDisabled}
        title={exportBlockReason}
        className="h-9 flex-1 rounded-lg border border-primary bg-primary text-sm font-medium text-white hover:bg-primary/90 hover:border-primary/70 focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
        data-testid={renderBusy ? 'video-editor-stop-render' : 'video-editor-export-button'}>
        {renderBusy ? t('videoEditorNode.stop') : t('timelineOverhaul.export')}
      </button>
    </div>
  )
}

function EditorPreviewSurface({
  projectId,
  nodeId,
  videoClips,
  audioClips,
  timelineConfig,
  hasReferences,
  videoCount,
  audioCount,
  progressMessage,
  error,
  status,
  isRenderBusy,
  hasPendingSources,
  waitingMessage,
  sourceErrorMessage,
}: {
  projectId: string
  nodeId: string
  videoClips: ClipReference[]
  audioClips: AudioClipReference[]
  timelineConfig?: TimelineRenderConfig
  hasReferences: boolean
  videoCount: number
  audioCount: number
  progressMessage?: string | null
  error?: string | null
  status: string
  isRenderBusy: boolean
  hasPendingSources: boolean
  waitingMessage?: string
  sourceErrorMessage?: string
}) {
  const { t } = useTranslation('canvas')

  if (isRenderBusy) {
    const metaLoading: MetaLoadingDescriptor = {
      message: progressMessage || waitingMessage || (status === 'pending_cost_confirmation'
        ? t('baseNode.statusPendingApproval')
        : status === 'pending_execution'
          ? t('baseNode.statusQueued')
          : t('videoEditorNode.statusRendering')),
      nodeType: 'videoEditor',
      loadingState: status === 'pending_cost_confirmation'
        ? 'pending-cost'
        : status === 'pending_execution'
          ? 'pending-execution'
          : 'running',
    }
    return (
      <MetaLoadingSurface
        {...metaLoading}
        projectId={projectId}
        nodeId={nodeId}
        aspectRatio={ASPECT_RATIO_MAP['16:9']}
        presentation="full"
      />
    )
  }

  if (sourceErrorMessage) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-destructive/40 bg-destructive/10 text-destructive">
          <Scissors className="h-5 w-5" />
        </div>
        <div
          role="alert"
          data-testid="video-editor-source-error"
          className="text-sm font-medium text-destructive"
        >
          {sourceErrorMessage}
        </div>
      </div>
    )
  }

  if (hasPendingSources) {
    return (
      <MetaLoadingSurface
        message={waitingMessage || t('videoEditorNode.statusWaiting')}
        nodeType="videoEditor"
        loadingState="pending-execution"
        projectId={projectId}
        nodeId={nodeId}
        aspectRatio={ASPECT_RATIO_MAP['16:9']}
        presentation="full"
      />
    )
  }

  if (videoClips.length > 0) {
    return (
      <div className="absolute inset-0 nodrag">
        <TimelinePreviewPlayer
          nodeId={nodeId}
          videoClips={videoClips}
          audioClips={audioClips}
          timelineConfig={timelineConfig}
          readOnly
          hideClipBadge
          // Keep canvas preview starts accepted by browser media policy; users
          // can explicitly unmute with the preview chrome.
          initialMuted
          renderOverlays={(controls) => (
            <PreviewChrome
              controls={controls}
              completedMessage={status === 'completed' && !error ? progressMessage : null}
            />
          )}
        />
        {error && (
          <NodeStatusMessage
            message={sanitizeNodeError(error, t, true)}
            testId="video-editor-card-error"
          />
        )}
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-1 border border-border-subtle text-muted-foreground">
        <Scissors className="w-5 h-5" />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium text-foreground">
          {hasPendingSources
            ? t('videoEditorNode.waitingForSourcesTitle')
            : hasReferences
              ? t('videoEditorNode.timelineConfiguration')
              : t('videoEditorNode.connectReferences')}
        </div>
        <div className="text-xs font-medium text-muted-foreground">
          {hasPendingSources && waitingMessage
            ? waitingMessage
            : hasReferences
            ? t('videoEditorNode.referencesConnected', {
                videoCount: t('videoEditorNode.videoReferenceCount', { count: videoCount }),
                audioCount: t('videoEditorNode.audioReferenceCount', { count: audioCount }),
              })
            : t('videoEditorNode.addVideoAndAudioReferences')}
        </div>
      </div>
      {/* Status overlay — the shared, neutral treatment pinned to the bottom of
          the preview surface (post 1634117708345526). */}
      {error && (
        <NodeStatusMessage
          message={sanitizeNodeError(error, t, true)}
          testId="video-editor-card-error"
        />
      )}
    </div>
  )
}

export function PreviewChrome({
  controls,
  completedMessage,
}: {
  controls: TimelinePreviewControls
  completedMessage: string | null | undefined
}) {
  const { t } = useTranslation('canvas')
  const {
    isPlaying,
    togglePlayPause,
    playheadPosition,
    totalDuration,
    setPlayhead,
    isMuted,
    toggleMute,
    resumeAudioContexts,
    previewElementRef,
  } = controls

  const [isFullscreen, setIsFullscreen] = useState(false)
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === previewElementRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [previewElementRef])

  const handleMute = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      const next = !isMuted
      chromeLog({ event: 'mute-toggle', muted: next })
      exportFallbackLog({ event: 'preview-mute-toggle', muted: next })
      if (!next) {
        resumeAudioContexts()
      }
      toggleMute()
    },
    [isMuted, resumeAudioContexts, toggleMute],
  )

  const handleFullscreen = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      const wrapper = previewElementRef.current
      if (!wrapper) return
      const entering = document.fullscreenElement !== wrapper
      chromeLog({ event: 'fullscreen-toggle', entering })
      try {
        if (entering) {
          await wrapper.requestFullscreen()
        } else {
          await document.exitFullscreen()
        }
        timelineQaLog({ event: entering ? 'fullscreen-entered' : 'fullscreen-exited' })
      } catch (err) {
        // Browsers may reject fullscreen in non-user-gesture contexts.
        // We still emit the trace so debugging is possible.
        // eslint-disable-next-line no-console
        console.warn('VideoEditor preview fullscreen rejected', err)
      }
    },
    [previewElementRef],
  )

  const handleScrub = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const t = parseFloat(event.target.value)
      if (Number.isFinite(t)) setPlayhead(t)
    },
    [setPlayhead],
  )

  const handlePlayClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      // When playback is parked at the end of the timeline, the next Play press
      // must rewind to the start first. Without this the RAF playback loop
      // (useTimelinePlayback) immediately re-detects position >= totalDuration
      // and stops again, so Play appears dead after the first full playthrough
      // and the preview shows only static poster frames (no active clip past the
      // end). Mirrors the Timeline modal's spacebar end-of-timeline reset.
      if (!isPlaying && totalDuration > 0 && playheadPosition >= totalDuration - 0.05) {
        chromeLog({
          event: 'canvas-play-reset-at-end',
          wasS: Number(playheadPosition.toFixed(3)),
          totalS: Number(totalDuration.toFixed(3)),
        })
        setPlayhead(0)
      }
      if (!isPlaying) {
        resumeAudioContexts()
      }
      togglePlayPause()
    },
    [isPlaying, totalDuration, playheadPosition, setPlayhead, resumeAudioContexts, togglePlayPause],
  )

  return (
    <>
      {/* Top-right mute + fullscreen */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        <button
          type="button"
          onClick={handleMute}
          className="h-7 w-7 rounded-md bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/10 text-foreground/85 hover:text-foreground flex items-center justify-center nodrag"
          data-testid="video-editor-preview-mute"
          title={isMuted ? t('videoEditorNode.unmute') : t('videoEditorNode.mute')}
        >
          {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={handleFullscreen}
          className="h-7 w-7 rounded-md bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/10 text-foreground/85 hover:text-foreground flex items-center justify-center nodrag"
          data-testid="video-editor-preview-fullscreen"
          title={isFullscreen ? t('videoEditorNode.exitFullscreen') : t('videoEditorNode.enterFullscreen')}
        >
          {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      </div>

      {completedMessage && (
        <div className="absolute top-12 right-2 z-10 max-w-[55%] bg-black/65 backdrop-blur-sm text-foreground text-xxs px-2 py-1 rounded truncate">
          {completedMessage}
        </div>
      )}

      {/* PREVIEW badge — bottom-left of preview frame, above bottom bar */}
      <div
        className="pointer-events-none absolute bottom-10 left-2 z-10 px-2 py-0.5 rounded-md bg-amber-500/85 text-black text-xxs font-bold tracking-wider shadow-md backdrop-blur-sm"
        data-testid="video-editor-preview-badge"
      >
        {t('videoEditorNode.preview')}
      </div>

      {/* Bottom timeline bar — VideoPreviewPlayer-style */}
      <div
        className="absolute bottom-0 left-0 right-0 z-10 px-2 py-1.5 bg-gradient-to-t from-black/80 to-transparent flex items-center gap-1.5 nodrag"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        data-testid="video-editor-bottom-bar"
      >
        <button
          type="button"
          onClick={handlePlayClick}
          className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-foreground hover:text-white transition-colors"
          data-testid="video-editor-preview-play"
          title={isPlaying ? t('videoEditorNode.pause') : t('videoEditorNode.play')}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>
        <span
          className="text-text-secondary text-xxs tabular-nums flex-shrink-0 select-none"
          data-testid="video-editor-current-time"
        >
          {formatPreviewTime(playheadPosition)}
        </span>
        <div className="flex-1 h-5 flex items-center group/scrub relative">
          <div className="w-full h-1 bg-white/20 rounded-full relative group-hover/scrub:h-1.5 transition-all">
            <div
              className="absolute top-0 left-0 h-full bg-white/70 rounded-full pointer-events-none"
              style={{
                width: `${
                  totalDuration > 0
                    ? Math.min(100, Math.max(0, (playheadPosition / totalDuration) * 100))
                    : 0
                }%`,
              }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/scrub:opacity-100 transition-opacity pointer-events-none"
              style={{
                left: `${
                  totalDuration > 0
                    ? Math.min(100, Math.max(0, (playheadPosition / totalDuration) * 100))
                    : 0
                }%`,
                transform: `translateX(-50%) translateY(-50%)`,
              }}
              data-testid="video-editor-preview-playhead"
            />
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(totalDuration, 0.01)}
            step={0.01}
            value={Math.min(playheadPosition, Math.max(totalDuration, 0.01))}
            onChange={handleScrub}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="video-editor-preview-scrubber"
            aria-label={t('videoEditorNode.seekVideoPreview')}
          />
        </div>
        <span
          className="text-text-secondary text-xxs tabular-nums flex-shrink-0 select-none"
          data-testid="video-editor-total-duration"
        >
          {formatPreviewTime(totalDuration)}
        </span>
      </div>
    </>
  )
}

/** Format seconds as M:SS — mirrors VideoPreviewPlayer's formatTime. */
function formatPreviewTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const VideoEditorNode = memo(function VideoEditorNode(props: NodeProps<VideoEditorNodeData>) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { id, data, selected, dragging } = props
  const { t } = useTranslation('canvas')
  const status = dataStatus(data)
  const rawEditSpecRevision = dataValue(data, 'revision')
  const editSpecRevision =
    typeof rawEditSpecRevision === 'number' && Number.isInteger(rawEditSpecRevision)
      ? rawEditSpecRevision
      : 0
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const error = dataStrOpt(data, 'error')
  const projectId = useProjectId()
  const readOnly = useReadOnly()
  const canvasId = useActiveCanvasId()
  const uploadFile = useUploadFile()
  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'videoEditor', data)
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const allNodes = useNodes<ReactFlowCanvasNodeData>()
  const reactFlow = useReactFlow()
  const { emitNuxEvent } = useNuxTooltipEvents()
  const { handleAddToChat, handleDelete, handleDuplicate } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, t('videoEditorNode.videoEditor'))

  const [isTimelineEditorOpen, setIsTimelineEditorOpen] = useState(false)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isExportSheetOpen, setIsExportSheetOpen] = useState(false)
  const [exportSheetDefaultName, setExportSheetDefaultName] = useState(() => buildDefaultExportName())
  const [isCanvasRenderStarting, setIsCanvasRenderStarting] = useState(false)
  const [dreamVibePreviewEnabled, setDreamVibePreviewEnabled] = useState(false)
  const [dreamVibeAuthoringEnabled, setDreamVibeAuthoringEnabled] = useState(false)
  const [dreamVibeRenderEnabled, setDreamVibeRenderEnabled] = useState(false)
  const statusRenderBusy = isVideoEditorRenderBusy(status)
  const renderBusy = statusRenderBusy || isCanvasRenderStarting
  const pickedEditorVideo = resolvePickedVideoAsset({ data })
  const { action: upscaleVideoAction, flyout: upscaleVideoFlyout } = useUpscaleVideoAction({
    nodeId: id,
    hasVideo: Boolean(pickedEditorVideo),
    isBusy: renderBusy,
    sourceName: node?.get('in_output_name') || t('videoEditorNode.videoEditor'),
    sourceDimensions: videoDimensionsFromMetadata(pickedEditorVideo?.entry.result?.metadata),
    sourceVideoPath: pickedEditorVideo?.path,
  })
  const isEditSpecPrimary = data.edit_spec_compatibility_state === 'editspec_primary'
  const referencesDisabled = renderBusy || (isEditSpecPrimary && !dreamVibeAuthoringEnabled)
  const renderLockTraceId = useMemo(() => `video-editor-render-lock:${id}`, [id])
  const renderLockStartStatusRef = useRef<string | null>(null)
  const renderLockPreviousStatusRef = useRef(status)
  const previousReferencesDisabledRef = useRef(referencesDisabled)

  useEffect(() => {
    if (!projectId || data.edit_spec_compatibility_state !== 'editspec_primary') {
      setDreamVibePreviewEnabled(false)
      setDreamVibeAuthoringEnabled(false)
      setDreamVibeRenderEnabled(false)
      return
    }
    const controller = new AbortController()
    void getDreamVibeCapabilities(projectId, canvasId, controller.signal)
      .then((capabilities) => {
        setDreamVibePreviewEnabled(capabilities.live_preview)
        setDreamVibeAuthoringEnabled(capabilities.authoring_tools)
        setDreamVibeRenderEnabled(capabilities.vibe_render)
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setDreamVibePreviewEnabled(false)
        setDreamVibeAuthoringEnabled(false)
        setDreamVibeRenderEnabled(false)
      })
    return () => controller.abort()
  }, [canvasId, data.edit_spec_compatibility_state, projectId])

  useEffect(() => {
    const previousStatus = renderLockPreviousStatusRef.current
    if (isCanvasRenderStarting && statusRenderBusy) {
      renderLockLog({
        event: 'render-lock-handed-to-status',
        trace_id: renderLockTraceId,
        nodeId: id,
        status,
        previousStatus,
        localLockWasActive: true,
      })
      setIsCanvasRenderStarting(false)
      renderLockStartStatusRef.current = null
    } else if (
      isCanvasRenderStarting &&
      renderLockStartStatusRef.current !== status &&
      (status === 'completed' || status === 'error')
    ) {
      renderLockLog({
        event: 'render-lock-released',
        trace_id: renderLockTraceId,
        reason: 'status-terminal',
        nodeId: id,
        status,
        previousStatus,
      })
      setIsCanvasRenderStarting(false)
      renderLockStartStatusRef.current = null
    }
    renderLockPreviousStatusRef.current = status
  }, [id, isCanvasRenderStarting, renderLockTraceId, status, statusRenderBusy])

  useEffect(() => {
    if (referencesDisabled && !previousReferencesDisabledRef.current) {
      renderLockLog({
        event: 'references-disabled',
        trace_id: renderLockTraceId,
        nodeId: id,
        status,
        statusRenderBusy,
        localRenderStarting: isCanvasRenderStarting,
      })
    }
    previousReferencesDisabledRef.current = referencesDisabled
  }, [id, isCanvasRenderStarting, referencesDisabled, renderLockTraceId, status, statusRenderBusy])

  const handleOpenReferenceSelector = useCallback(() => {
    if (referencesDisabled) {
      timelineQaLog({
        event: 'add-references-blocked',
        nodeId: id,
        status,
      })
      renderLockLog({
        event: 'add-references-blocked',
        trace_id: renderLockTraceId,
        origin: 'canvas-rail',
        nodeId: id,
        status,
        statusRenderBusy,
        localRenderStarting: isCanvasRenderStarting,
      })
      return
    }
    timelineQaLog({
      event: 'add-references-opened',
      nodeId: id,
      status,
    })
    setIsRefSelectorOpen(true)
  }, [referencesDisabled, renderLockTraceId, id, status, statusRenderBusy, isCanvasRenderStarting])

  useEffect(() => {
    if (referencesDisabled && isRefSelectorOpen) {
      timelineQaLog({
        event: 'add-references-closed-during-render',
        nodeId: id,
        status,
      })
      setIsRefSelectorOpen(false)
    }
  }, [referencesDisabled, status, isRefSelectorOpen, id])

  const connectedVideoEdges = useMemo(
    () => [...edges.filter(
      (edge) =>
        edge.target === id &&
        (
          edge.targetHandle === 'in_videos'
          || edge.targetHandle === 'in_video'
          || isReferenceMediaEdge(edge, allNodes, 'video')
        ),
    )].sort((left, right) => {
      const leftIndex = typeof left.data?.toIndex === 'number'
        ? left.data.toIndex
        : Number.MAX_SAFE_INTEGER
      const rightIndex = typeof right.data?.toIndex === 'number'
        ? right.data.toIndex
        : Number.MAX_SAFE_INTEGER
      return leftIndex - rightIndex
    }),
    [edges, id, allNodes],
  )
  const connectedAudioEdges = useMemo(
    () => [...edges.filter(
      (edge) =>
        edge.target === id &&
        (
          edge.targetHandle === 'in_audios'
          || edge.targetHandle === 'in_audio'
          || isReferenceMediaEdge(edge, allNodes, 'audio')
        ),
    )].sort((left, right) => {
      const leftIndex = typeof left.data?.toIndex === 'number'
        ? left.data.toIndex
        : Number.MAX_SAFE_INTEGER
      const rightIndex = typeof right.data?.toIndex === 'number'
        ? right.data.toIndex
        : Number.MAX_SAFE_INTEGER
      return leftIndex - rightIndex
    }),
    [edges, id, allNodes],
  )
  const connectedVideoSourceIds = useMemo(
    () => [...new Set(connectedVideoEdges.map((edge) => edge.source))],
    [connectedVideoEdges],
  )
  const connectedAudioSourceIds = useMemo(
    () => [...new Set(connectedAudioEdges.map((edge) => edge.source))],
    [connectedAudioEdges],
  )

  const resolvedVideoClips = useMemo(() => {
    const clips: ClipReference[] = []
    for (const edge of connectedVideoEdges) {
      const sourceNode = allNodes.find((n) => n.id === edge.source)
      const pickedVideo = resolvePickedVideoAsset(sourceNode)

      if (pickedVideo) {
        const outputDuration = dataValue(sourceNode?.data, 'out_video_duration')
        const sourceTimelineDuration = timelineRenderConfigDuration(
          normalizeTimelineRenderConfig(dataValue(sourceNode?.data, 'in_video_config')),
        )
        const duration =
          coerceTimelineDurationHint(typeof outputDuration === 'number' ? outputDuration : undefined) ??
          sourceTimelineDuration ??
          coerceTimelineDurationHint(pickedVideo.entry.metadata?.duration_seconds) ??
          coerceTimelineDurationHint(pickedVideo.entry.result?.metadata?.duration_seconds)
        clips.push({
          id: edge.source,
          path: pickedVideo.path,
          name: pickedVideo.path.split('/').pop() || t('videoEditorNode.untitled'),
          duration,
        })
      }
    }
    return clips
  }, [connectedVideoEdges, allNodes, t])

  const resolvedAudioClips = useMemo(() => {
    const clips: AudioClipReference[] = []
    for (const edge of connectedAudioEdges) {
      const sourceNode = allNodes.find((n) => n.id === edge.source)
      const audioPath = getAudioAssetPath(sourceNode?.type ?? '', sourceNode?.data)

      if (audioPath) {
        const outputDuration = dataValue(sourceNode?.data, 'out_duration_seconds')
        const duration = coerceTimelineDurationHint(
          typeof outputDuration === 'number' ? outputDuration : undefined,
        )
        clips.push({
          id: edge.source,
          path: audioPath,
          name: sourceNode ? getAudioReferenceLabel(sourceNode, audioPath) : audioPath.split('/').pop() || t('videoEditorNode.untitled'),
          duration,
          audioKind: getAudioKind(sourceNode),
        })
      }
    }
    return clips
  }, [connectedAudioEdges, allNodes, t])

  useEffect(() => {
    if (!readOnly) return

    const resolvedSourceIds = new Set(resolvedVideoClips.map((clip) => clip.id))
    const unresolvedSourceNodeIds = connectedVideoEdges
      .filter((edge) => !resolvedSourceIds.has(edge.source))
      .map((edge) => edge.source)
    const logTimer = window.setTimeout(() => {
      publishedHydrationLog({
        event: unresolvedSourceNodeIds.length > 0 ? 'clips-incomplete' : 'clips-hydrated',
        trace_id: `published-video-editor-hydration:${projectId ?? 'unknown'}`,
        projectId,
        nodeId: id,
        edgeCount: connectedVideoEdges.length,
        clipCount: resolvedVideoClips.length,
        sources: connectedVideoEdges.map((edge) => edge.source).join(','),
        toIndexes: connectedVideoEdges.map((edge) => edge.data?.toIndex ?? 'unset').join(','),
        incomingVideoEdgeCount: connectedVideoEdges.length,
        resolvedClipCount: resolvedVideoClips.length,
        orderedSourceNodeIds: connectedVideoEdges.map((edge) => edge.source).join(','),
        orderedToIndices: connectedVideoEdges
          .map((edge) => edge.data?.toIndex ?? 'unset')
          .join(','),
        unresolvedSourceNodeIds: unresolvedSourceNodeIds.join(','),
      })
    }, 0)
    return () => window.clearTimeout(logTimer)
  }, [connectedVideoEdges, id, projectId, readOnly, resolvedVideoClips])

  const dreamVibeHistoricalSources = useMemo(() => {
    const sources: Record<string, string> = {}
    const graphicSourceIds = new Set(
      (data.in_edit_spec?.overlay_clips ?? []).flatMap((overlay) =>
        (overlay.graphic_sources ?? []).map((source) => source.node_id),
      ),
    )
    const sourceIds = new Set(
      [
        ...edges.filter((edge) => edge.target === id).map((edge) => edge.source),
        ...graphicSourceIds,
      ],
    )
    for (const sourceId of sourceIds) {
      const sourceNode = allNodes.find((node) => node.id === sourceId)
      if (!sourceNode) continue
      const videoOutput = getNodeVideoOutput(sourceNode)
      for (const [index, entry] of (videoOutput?.history ?? []).entries()) {
        const path = entry.result?.local_path
        if (path) sources[`${sourceId}.out_video@${index}`] = path
      }
      const audioHistories = [
        getTypedNodeData(sourceNode, 'soundExpert')?.out_audio?.history ?? [],
        getTypedNodeData(sourceNode, 'audio')?.out_audio?.history ?? [],
      ]
      for (const history of audioHistories) {
        for (const [index, entry] of history.entries()) {
          const path = entry.result?.local_path
          if (path) sources[`${sourceId}.out_audio@${index}`] = path
        }
      }
      const imageOutputs = [
        getTypedNodeData(sourceNode, 'image')?.out_image,
        getTypedNodeData(sourceNode, 'imageGenerator')?.out_image,
        getTypedNodeData(sourceNode, 'webtoonPanelGenerator')?.out_image,
      ]
      for (const output of imageOutputs) {
        const history = output?.history ?? []
        for (const [index, entry] of history.entries()) {
          const path = entry.result?.local_path
          if (path) sources[`${sourceId}.out_image@${index}`] = path
        }
        const picked = output?.picked_index ?? 0
        const currentPath = history[picked]?.result?.local_path
        if (currentPath) sources[`${sourceId}.out_image`] = currentPath
      }
    }
    return sources
  }, [allNodes, data.in_edit_spec, edges, id])

  const importedMedia = useMemo(() => buildOrderedReferenceMedia(allNodes, edges, id, [
    ['in_videos', 'video'], ['in_audios', 'audio'], ['in_audio', 'audio'],
  ]), [allNodes, edges, id])
  const currentReferences = useMemo(() => dedupeReferences(importedMedia), [importedMedia])

  const displayedReferenceOrder = currentReferences.map(referenceMediaKey).join(' -> ')
  useEffect(() => {
    referenceMediaOrderLog({ event: 'reference-media:displayed',
      trace_id: `reference-media:${projectId}:${id}`, project_id: projectId,
      node_id: id, node_type: 'video_editor',
      reference_key_format: 'media:source_node:source_port:array_index (empty = whole output)',
      ordered_references: displayedReferenceOrder })
  }, [id, projectId, displayedReferenceOrder])

  const importingRef = useRef(false)
  const [isImporting, setIsImporting] = useState(false)
  const latestImportRevision = useRef(editSpecRevision)
  latestImportRevision.current = editSpecRevision
  const commitImportedSources = useCallback(async (selected: SelectedReference[]) => {
    const spec = data.in_edit_spec ? ydocSafeCloneNodeData(data.in_edit_spec) : undefined
    if (data.edit_spec_compatibility_state !== 'editspec_primary' || !spec) return
    if (!projectId) throw new Error('A project is required to import media')
    const revision = editSpecRevision
    const next = await appendEditSpecMedia(spec, selected, projectId)
    if (next === spec) return
    if (latestImportRevision.current !== revision) throw new Error('The edit changed while media was loading. Try importing again.')
    await writeVideoEditorEditSpec(projectId, id, { editSpec: next, expectedRevision: revision,
      expectedFingerprint: await fingerprintDreamwriteEditSpec(spec), canvasId })
  }, [data.in_edit_spec, data.edit_spec_compatibility_state, projectId, id, canvasId, editSpecRevision])
  const handleRefSave = useCallback(
    async (selected: SelectedReference[]) => {
      setIsRefSelectorOpen(false)

      const refs = dedupeReferences(selected)
      const videoRefs = refs.filter((ref) => ref.mediaType === 'video')
      const audioRefs = refs.filter((ref) => ref.mediaType === 'audio')

      if (referencesDisabled) {
        renderLockLog({
          event: 'add-references-save-blocked',
          trace_id: renderLockTraceId,
          nodeId: id,
          status,
          statusRenderBusy,
          localRenderStarting: isCanvasRenderStarting,
          selectedCount: selected.length,
          videoRefCount: videoRefs.length,
          audioRefCount: audioRefs.length,
        })
        return
      }

      if (importingRef.current) return
      importingRef.current = true
      setIsImporting(true)
      try {
      await commitImportedSources(refs)
      if (canvas) replaceReferenceMediaEdges(canvas, id, refs, ['in_videos', 'in_audios', 'in_audio'])
      referenceMediaOrderLog({ event: 'reference-media:edges-updated', node_id: id, node_type: 'video_editor',
          trace_id: `reference-media:${projectId}:${id}`, project_id: projectId,
          target_port: 'in_reference_media',
        ordered_references: refs.map(referenceMediaKey).join(' -> ') })
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause)
        renderLockLog({ event: 'reference-import-failed', nodeId: id, error: message })
        toast.error(message)
      }
      finally { importingRef.current = false; setIsImporting(false) }
    },
    [canvas, id, referencesDisabled, renderLockTraceId, status, statusRenderBusy, isCanvasRenderStarting, commitImportedSources, projectId],
  )

  const previousImportedKeys = useRef(new Set(currentReferences.map(referenceKey)))
  useEffect(() => {
    const connectedKeys = new Set(currentReferences.map(referenceKey))
    previousImportedKeys.current = new Set([...previousImportedKeys.current].filter((key) => connectedKeys.has(key)))
    const additions = currentReferences.filter((item) => !previousImportedKeys.current.has(referenceKey(item)))
    if (!additions.length || importingRef.current || referencesDisabled) return
    previousImportedKeys.current = connectedKeys
    importingRef.current = true; setIsImporting(true)
    void commitImportedSources(additions).catch((cause) => toast.error(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => { importingRef.current = false; setIsImporting(false) })
  }, [currentReferences, commitImportedSources, referencesDisabled, isImporting])

  const statusConfig = getStatusConfig(renderBusy ? 'running' : error ? 'error' : status, error, progressMessage)
  const STATUS_TEXT_I18N: Record<string, string> = useMemo(() => ({
    'Ready': t('videoEditorNode.statusReady'),
    'Waiting': t('videoEditorNode.statusWaiting'),
    'Confirm cost': t('videoEditorNode.statusConfirmCost'),
    'Rendering...': t('videoEditorNode.statusRendering'),
    'Done': t('videoEditorNode.statusDone'),
    'Error': t('videoEditorNode.statusError'),
    'Stopped': t('videoEditorNode.statusStopped'),
  }), [t])
  const translatedStatusText = STATUS_TEXT_I18N[statusConfig.text] ?? statusConfig.text
  const stoppedRender = isStoppedRender(status, error, progressMessage)
  const timelineConfig = normalizeTimelineRenderConfig(
    data.in_video_config ?? node?.get('in_video_config'),
  )
  const savedTimelineVideoClips = useMemo(
    () => savedTimelineVideoReferences(timelineConfig, t('videoEditorNode.untitled')),
    [timelineConfig, t],
  )
  const previewVideoClips = resolvedVideoClips.length > 0 ? resolvedVideoClips : savedTimelineVideoClips
  const hasClips = resolvedVideoClips.length > 0
  const hasSavedTimelineVideo = savedTimelineVideoClips.length > 0
  const hasRenderableClips = hasClips || hasSavedTimelineVideo || !!timelineConfig?.timeline.text_overlays?.length
  const missingVideoSourceIds = useMemo(() => {
    const resolved = new Set(resolvedVideoClips.map((clip) => clip.id))
    return connectedVideoSourceIds.filter((sourceId) => !resolved.has(sourceId))
  }, [connectedVideoSourceIds, resolvedVideoClips])
  const missingAudioSourceIds = useMemo(() => {
    const resolved = new Set(resolvedAudioClips.map((clip) => clip.id))
    return connectedAudioSourceIds.filter((sourceId) => !resolved.has(sourceId))
  }, [connectedAudioSourceIds, resolvedAudioClips])
  const failedSourceIds = useMemo(
    () => [...missingVideoSourceIds, ...missingAudioSourceIds].filter((sourceId) => {
      const source = allNodes.find((candidate) => candidate.id === sourceId)
      return source ? dataStatus(source.data) === 'error' : false
    }),
    [allNodes, missingAudioSourceIds, missingVideoSourceIds],
  )
  const failedSourceIdSet = useMemo(() => new Set(failedSourceIds), [failedSourceIds])
  const unresolvedVideoSourceIds = useMemo(
    () => missingVideoSourceIds.filter((sourceId) => !failedSourceIdSet.has(sourceId)),
    [failedSourceIdSet, missingVideoSourceIds],
  )
  const unresolvedAudioSourceIds = useMemo(
    () => missingAudioSourceIds.filter((sourceId) => !failedSourceIdSet.has(sourceId)),
    [failedSourceIdSet, missingAudioSourceIds],
  )
  const hasPendingSources = unresolvedVideoSourceIds.length > 0 || unresolvedAudioSourceIds.length > 0
  const sourceErrorMessage = failedSourceIds.length > 0
    ? t('videoEditorNode.connectedSourceFailed')
    : undefined
  const waitingMessage = useMemo(() => {
    if (!hasPendingSources) return undefined
    if (unresolvedVideoSourceIds.length > 0 && unresolvedAudioSourceIds.length > 0) {
      return t('videoEditorNode.waitingForVideoAndAudio')
    }
    if (unresolvedVideoSourceIds.length > 0) {
      return t('videoEditorNode.waitingForVideo')
    }
    return t('videoEditorNode.waitingForNarrationAudio')
  }, [hasPendingSources, t, unresolvedAudioSourceIds.length, unresolvedVideoSourceIds.length])
  const hasConnectedSources = connectedVideoSourceIds.length > 0 || connectedAudioSourceIds.length > 0
  const hasReferences = hasRenderableClips || resolvedAudioClips.length > 0 || hasConnectedSources
  const displayedVideoCount = Math.max(resolvedVideoClips.length, connectedVideoSourceIds.length)
  const displayedAudioCount = Math.max(resolvedAudioClips.length, connectedAudioSourceIds.length)
  const primaryEditSpec = data.in_edit_spec
  const exportDuration = useMemo(() => {
    if (isEditSpecPrimary) {
      return primaryEditSpec ? dreamwriteEditSpecDurationSeconds(primaryEditSpec) : undefined
    }
    const configuredDuration = timelineRenderConfigDuration(timelineConfig)
    if (configuredDuration !== undefined) return configuredDuration
    if (resolvedVideoClips.length > 0) {
      if (resolvedVideoClips.some((clip) => clip.duration === undefined)) return undefined
      return resolvedVideoClips.reduce((total, clip) => total + (clip.duration ?? 0), 0)
    }
    if (resolvedAudioClips.some((clip) => clip.duration === undefined)) return undefined
    return resolvedAudioClips.reduce((total, clip) => total + (clip.duration ?? 0), 0)
  }, [isEditSpecPrimary, primaryEditSpec, resolvedAudioClips, resolvedVideoClips, timelineConfig])
  const exportDurationTooShort = isVideoExportDurationTooShort(exportDuration)
  const minimumExportDurationMessage = t('videoEditorNode.minimumExportDuration')
  const renderDisabled = isEditSpecPrimary && !dreamVibeRenderEnabled
  const exportBlockReason =
    sourceErrorMessage ?? waitingMessage ?? (exportDurationTooShort ? minimumExportDurationMessage : undefined)
  const showSourceFailureState = failedSourceIds.length > 0 && !renderBusy
  const showPendingSourceState = hasPendingSources && !showSourceFailureState && !renderBusy && status !== 'error'
  const editorAccessBlocked = hasPendingSources
  const visibleStatusText = showSourceFailureState
    ? t('videoEditorNode.statusError')
    : showPendingSourceState
      ? t('videoEditorNode.statusWaiting')
      : translatedStatusText
  const visibleStatusDot = showSourceFailureState
    ? STATUS_CLASS.error.dot
    : showPendingSourceState
      ? STATUS_CLASS.pending_execution.dot
      : statusConfig.dot

  useEffect(() => {
    pendingSourcesLog({
      event: 'pending-sources:state',
      nodeId: id,
      active: showPendingSourceState,
      unresolvedVideoCount: unresolvedVideoSourceIds.length,
      unresolvedAudioCount: unresolvedAudioSourceIds.length,
      failedSourceCount: failedSourceIds.length,
      editorActionDisabled: editorAccessBlocked,
    })
  }, [editorAccessBlocked, failedSourceIds.length, id, showPendingSourceState, unresolvedAudioSourceIds.length, unresolvedVideoSourceIds.length])

  // Existing labels on the canvas — used for duplicate-name validation
  // in the Export confirm sheet. Excludes this editor node's own label.
  const existingLabels = useMemo(() => {
    const labels = new Set<string>()
    for (const node of allNodes) {
      if (node.id === id) continue
      const rawLabel = node.data?.label
      const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : node.id
      if (label) labels.add(label)
    }
    return labels
  }, [allNodes, id])

  // Open the Export confirm sheet.
  const handleOpenExportSheet = useCallback(() => {
    if (!projectId || renderBusy) return
    if (isEditSpecPrimary && !dreamVibeRenderEnabled) return
    if (sourceErrorMessage) {
      exportFlowLog({ event: 'open-export-blocked-source-error', nodeId: id })
      toast.error(sourceErrorMessage)
      return
    }
    if (hasPendingSources && waitingMessage) {
      exportFlowLog({
        event: 'export-blocked-pending-sources',
        origin: 'canvas',
        nodeId: id,
        unresolvedVideoCount: unresolvedVideoSourceIds.length,
        unresolvedAudioCount: unresolvedAudioSourceIds.length,
      })
      toast.error(waitingMessage)
      return
    }
    if (exportDurationTooShort) {
      exportFlowLog({
        event: 'export-blocked-minimum-duration',
        origin: 'canvas',
        nodeId: id,
        durationSeconds: exportDuration,
      })
      toast.error(minimumExportDurationMessage)
      return
    }
    const defaultName = buildDefaultExportName()
    setExportSheetDefaultName(defaultName)
    setIsExportSheetOpen(true)
    exportFlowLog({
      event: 'open-export-sheet',
      origin: 'canvas',
      nodeId: id,
      defaultName,
      existingLabelsCount: existingLabels.size,
    })
    exportFallbackLog({
      event: 'open-export-sheet',
      nodeId: id,
      hasVideoClips: hasClips,
      hasAudioClips: resolvedAudioClips.length > 0,
      hasTimelineConfig: Boolean(timelineConfig),
    })
  }, [
    dreamVibeRenderEnabled,
    existingLabels,
    hasClips,
    resolvedAudioClips.length,
    id,
    isEditSpecPrimary,
    projectId,
    renderBusy,
    hasPendingSources,
    sourceErrorMessage,
    waitingMessage,
    unresolvedAudioSourceIds.length,
    unresolvedVideoSourceIds.length,
    timelineConfig,
    exportDuration,
    exportDurationTooShort,
    minimumExportDurationMessage,
  ])

  // Confirm sheet → write the chosen name to YDoc, then dispatch executeNode.
  const handleConfirmExport = useCallback(
    async (chosenName: string) => {
      if (!projectId || renderBusy) return
      if (isEditSpecPrimary && !dreamVibeRenderEnabled) return
      if (sourceErrorMessage) {
        exportFlowLog({ event: 'confirm-export-blocked-source-error', nodeId: id })
        toast.error(sourceErrorMessage)
        setIsExportSheetOpen(false)
        return
      }
      if (hasPendingSources && waitingMessage) {
        exportFlowLog({
          event: 'export-blocked-pending-sources',
          origin: 'canvas-confirm',
          nodeId: id,
          unresolvedVideoCount: unresolvedVideoSourceIds.length,
          unresolvedAudioCount: unresolvedAudioSourceIds.length,
        })
        toast.error(waitingMessage)
        setIsExportSheetOpen(false)
        return
      }
      if (exportDurationTooShort) {
        exportFlowLog({
          event: 'export-blocked-minimum-duration',
          origin: 'canvas-confirm',
          nodeId: id,
          durationSeconds: exportDuration,
        })
        toast.error(minimumExportDurationMessage)
        setIsExportSheetOpen(false)
        return
      }
      setIsCanvasRenderStarting(true)
      renderLockStartStatusRef.current = status
      setIsExportSheetOpen(false)
      const action = isEditSpecPrimary ? 'vibe_render' : 'timeline_render'
      const inputUpdates = isEditSpecPrimary
        ? { in_export_name: chosenName }
        : timelineConfig
          ? { in_video_config: timelineConfig, in_export_name: chosenName }
          : { in_export_name: chosenName }
      renderLockLog({
        event: 'render-lock-engaged',
        trace_id: renderLockTraceId,
        origin: 'canvas',
        nodeId: id,
        projectId,
        status,
        chosenName,
        hasTimelineConfig: Boolean(timelineConfig),
        hasReferences,
      })
      exportFlowLog({
        event: 'confirm-export',
        nodeId: id,
        chosenName,
        hasTimelineConfig: Boolean(timelineConfig),
      })
      exportFallbackLog({
        event: 'confirm-export',
        nodeId: id,
        chosenName,
        hasTimelineConfig: Boolean(timelineConfig),
        usesDefaultTimelineFallback: !timelineConfig,
      })
      try {
        if (isEditSpecPrimary && !primaryEditSpec) {
          throw new Error('editspec_primary export requires in_edit_spec')
        }
        node?.writeInExportName(chosenName)
        renderLog({
          event: 'canvas-render-requested',
          nodeId: id,
          projectId,
          chosenName,
          hasInputUpdates: true,
          hasTimelineConfig: Boolean(timelineConfig),
        })
        await executeNode(projectId, id, { action, inputUpdates, canvasId })
        renderLog({ event: 'canvas-render-dispatched', nodeId: id, hasInputUpdates: true })
      } catch (err) {
        setIsCanvasRenderStarting(false)
        renderLockStartStatusRef.current = null
        renderLockLog({
          event: 'render-lock-released',
          trace_id: renderLockTraceId,
          reason: 'execute-failed',
          nodeId: id,
          status,
          error: String(err),
        })
        renderLog({ event: 'canvas-render-failed', nodeId: id, error: String(err) })
        console.error('Canvas render failed:', err)
      }
    },
    [projectId, canvasId, renderBusy, status, id, timelineConfig, isEditSpecPrimary, primaryEditSpec, hasReferences, node, renderLockTraceId, dreamVibeRenderEnabled, exportDuration, exportDurationTooShort, minimumExportDurationMessage, hasPendingSources, sourceErrorMessage, waitingMessage, unresolvedAudioSourceIds.length, unresolvedVideoSourceIds.length],
  )

  // Cancel the confirm sheet (no execute).
  const handleCancelExport = useCallback(() => {
    setIsExportSheetOpen(false)
    exportFlowLog({ event: 'cancel-export-sheet', nodeId: id })
  }, [id])

  // Chip click in the success line → fit the new node into view + select it.
  const handleExportChipClick = useCallback(
    (exportNodeId: string) => {
      const node = reactFlow.getNode(exportNodeId)
      exportFlowLog({ event: 'chip-click', nodeId: id, exportNodeId, found: !!node })
      if (!node) return
      reactFlow.fitView({ nodes: [node], padding: 0.3, duration: 500 })
      reactFlow.setNodes((nodes) =>
        nodes.map((n) => ({ ...n, selected: n.id === exportNodeId })),
      )
    },
    [reactFlow, id],
  )

  // project_editor_viewed (analytics doc name): fires on the user's Open editor click.
  const openTimelineEditor = useCallback((entryPoint: 'node_preview_button' | 'node_actions_button' | 'node_toolbar') => {
    if (editorAccessBlocked) {
      pendingSourcesLog({ event: 'pending-sources:open-blocked', nodeId: id, entryPoint })
      return
    }
    trackEditorOpened({ projectId, canvasId, nodeId: id, entryPoint, editable: !readOnly })
    emitNuxEvent({ type: 'editorOpened', editorId: 'timeline' })
    setIsTimelineEditorOpen(true)
  }, [canvasId, editorAccessBlocked, emitNuxEvent, id, projectId, readOnly])

  // Canvas cancel handler
  const handleCancelRender = useCallback(async () => {
    if (!projectId || isCancelling) return
    renderLog({ event: 'canvas-cancel-requested', nodeId: id })
    trackGenerationCancelled({ projectId, nodeId: id, nodeType: 'video_editor', stage: 'running' })
    setIsCancelling(true)
    try {
      await cancelNodeExecution(projectId, id, canvasId)
      renderLog({ event: 'canvas-cancel-dispatched', nodeId: id })
    } catch (err) {
      renderLog({ event: 'canvas-cancel-failed', nodeId: id, error: String(err) })
      console.error('Cancel render failed:', err)
    } finally {
      setIsCancelling(false)
    }
  }, [projectId, canvasId, id, isCancelling])

  // Persisted export-success state.  `last_export_node_id` is written by
  // the backend agent after a successful export; the chip label is looked
  // up live from the canvas so renames stay in sync.
  const exportSuccess = useMemo(() => {
    const rawExportNodeId = data.last_export_node_id
    const exportNodeId = typeof rawExportNodeId === 'string' ? rawExportNodeId : undefined
    // The pointer can survive old failed/recovered records or arrive before the
    // terminal status. It proves a historical export, not this attempt's success.
    if (!exportNodeId || status !== 'completed' || error || renderBusy) return null
    const exportNode = allNodes.find((n) => n.id === exportNodeId)
    if (!exportNode) {
      // Node may not have streamed in yet (or was deleted) — still surface
      // the success line keyed by node id so reload doesn't drop it.
      return { nodeId: exportNodeId, label: exportNodeId }
    }
    const rawLabel = exportNode.data?.label
    const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : exportNodeId
    return { nodeId: exportNodeId, label }
  }, [data.last_export_node_id, allNodes, status, error, renderBusy])

  useEffect(() => {
    exportStateLog({
      event: 'export-state-presented',
      nodeId: id,
      status,
      hasError: Boolean(error),
      renderBusy,
      lastExportNodeId: data.last_export_node_id ?? null,
      currentSuccessVisible: exportSuccess !== null,
    })
  }, [id, status, error, renderBusy, data.last_export_node_id, exportSuccess])

  // Trace render-completion transitions for log-based debugging.  Note:
  // the visible success indicator is `exportSuccess` (derived from
  // YDoc-persisted `last_export_node_id`) — there is no toast and no
  // auto-fitView here, so the user is in control of any pan/zoom.
  const prevStatusRef = useRef(status)
  useEffect(() => {
    if (status === 'running' && prevStatusRef.current !== 'running') {
      renderLog({ event: 'render-status-running', nodeId: id })
    }
    if (status === 'completed' && prevStatusRef.current === 'running') {
      renderLog({
        event: 'render-status-completed',
        nodeId: id,
        lastExportNodeId: data.last_export_node_id ?? null,
      })
      exportFlowLog({
        event: 'render-completed',
        nodeId: id,
        lastExportNodeId: data.last_export_node_id ?? null,
      })
    }
    prevStatusRef.current = status
  }, [status, id, data.last_export_node_id])

  // Trace persisted-success rehydration on mount so debug logs show
  // whether the success line survived a reload.
  const prevExportNodeIdRef = useRef(
    typeof data.last_export_node_id === 'string' ? data.last_export_node_id : null,
  )
  useEffect(() => {
    const currentExportNodeId = typeof data.last_export_node_id === 'string' ? data.last_export_node_id : null
    const previousExportNodeId = prevExportNodeIdRef.current
    if (exportSuccess) {
      exportFlowLog({
        event: 'rehydrate-success',
        nodeId: id,
        exportNodeId: exportSuccess.nodeId,
        label: exportSuccess.label,
      })
      if (currentExportNodeId && currentExportNodeId !== previousExportNodeId) {
        emitNuxEvent({ type: 'exportCompleted', editorId: 'timeline' })
      }
    }
    prevExportNodeIdRef.current = currentExportNodeId
    // Run once per `last_export_node_id` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.last_export_node_id])

  const toolbarActions = useMemo<ToolbarAction[]>(
    () => [
      {
        key: 'open-editor',
        label: t('videoEditorNode.openEditor'),
        icon: <Scissors className="w-3.5 h-3.5" />,
        onClick: () => openTimelineEditor('node_toolbar'),
        variant: 'primary',
        disabled: editorAccessBlocked,
        disabledReason: editorAccessBlocked ? waitingMessage : undefined,
      },
      upscaleVideoAction,
      {
        key: 'addToChat',
        label: t('videoEditorNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'duplicate',
        label: t('videoEditorNode.duplicate'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('videoEditorNode.delete'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [upscaleVideoAction, t, editorAccessBlocked, waitingMessage, handleAddToChat, duplicateMenuItems, handleDelete, openTimelineEditor],
  )

  return (
    <div className="relative">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!readOnly && !!selected && !dragging} />

      <div
        className={`${NODE_CARD_RADIUS_CLASS} shadow transition-all bg-card border group relative ${
          selected ? 'ring-2 ring-white/70 border-white/40' : 'border-border'
        }`}
        style={{ width: nodePresentationWidth(NODE_WIDTH) }}
        data-testid="video-editor-node-card"
      >
        {handlesVisible && (
          <>
            <Handle
              type="target"
              position={Position.Top}
              id="in_reference_media"
              className="!bg-transparent !border-0 !w-4 !h-1"
            />
            <Handle
              type="target"
              position={Position.Left}
              id="in_audios"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '50%' }}
            />
            <Handle
              type="target"
              position={Position.Left}
              id="in_audio"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '62%' }}
            />
            <Handle
              type="source"
              position={Position.Bottom}
              id="out_video"
              className="!bg-transparent !border-0 !w-4 !h-1"
            />
          </>
        )}

        <div className="px-3 py-2 bg-card-hover border-b border-border flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground">{t('videoEditorNode.videoEditor')}</span>
            {hasReferences && (
              <span className="text-xxs text-muted-foreground bg-surface-1 px-1.5 py-0.5 rounded">
                {t('videoEditorNode.videoAudioCount', { videoCount: displayedVideoCount, audioCount: displayedAudioCount })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xxs text-muted-foreground">{visibleStatusText}</span>
            <div className={`w-2.5 h-2.5 rounded-full ${visibleStatusDot}`} />
          </div>
        </div>

        <div
          className="relative w-full bg-canvas overflow-hidden"
          style={{ aspectRatio: ASPECT_RATIO_MAP['16:9'] }}
          data-testid="video-editor-config-surface"
        >
          <EditorPreviewSurface
            projectId={projectId}
            nodeId={id}
            videoClips={previewVideoClips}
            audioClips={resolvedAudioClips}
            timelineConfig={timelineConfig}
            hasReferences={hasReferences}
            videoCount={displayedVideoCount}
            audioCount={displayedAudioCount}
            progressMessage={progressMessage}
            error={stoppedRender ? null : error}
            status={status}
            isRenderBusy={renderBusy}
            hasPendingSources={showPendingSourceState}
            waitingMessage={waitingMessage}
            sourceErrorMessage={showSourceFailureState ? sourceErrorMessage : undefined}
          />
        </div>
        {!readOnly && <div className="px-3 py-2">
          <VideoReferencePicker nodeId={id} references={currentReferences} projectId={projectId}
            canSelectReferences={!referencesDisabled} onOpenSelector={handleOpenReferenceSelector}
            onReorderReferences={refs => { void handleRefSave(refs) }}
            onRemoveReference={ref => { void handleRefSave(currentReferences.filter(item => referenceMediaKey(item) !== referenceMediaKey(ref))) }} />
        </div>}
        {!readOnly && <EditorActions
          renderBusy={renderBusy} renderDisabled={renderDisabled}
          hasClips={hasRenderableClips} isCancelling={isCancelling}
          exportBlockReason={exportBlockReason}
          openEditorDisabled={editorAccessBlocked}
          openEditorDisabledReason={editorAccessBlocked ? waitingMessage : undefined}
          onOpenEditor={() => openTimelineEditor('node_actions_button')} onRender={handleOpenExportSheet}
          onCancelRender={handleCancelRender}
        />}
        {exportSuccess && <div className="px-3 pb-2 text-xs font-medium text-success" data-testid="video-editor-export-success">
          <button type="button" onClick={() => handleExportChipClick(exportSuccess.nodeId)}
            className="underline underline-offset-2" data-testid="video-editor-export-success-chip">
            {t('videoEditorNode.exportSucceed')} {exportSuccess.label}
          </button>
        </div>}
      </div>

      {!readOnly && isTimelineEditorOpen && (
        <TimelineEditorModal
          nodeId={id}
          videoClips={resolvedVideoClips}
          audioClips={resolvedAudioClips}
          timelineConfig={timelineConfig}
          connectedVideoSourceIds={connectedVideoSourceIds}
          connectedAudioSourceIds={connectedAudioSourceIds}
          dreamVibeHistoricalSources={dreamVibeHistoricalSources}
          renderStarting={isCanvasRenderStarting}
          nodeStatus={status}
          nodeProgressMessage={progressMessage}
          editSpec={data.in_edit_spec}
          editSpecCompatibilityState={data.edit_spec_compatibility_state}
          renderedSpecHash={data.out_rendered_spec_hash}
          editSpecRevision={editSpecRevision}
          dreamVibePreviewEnabled={dreamVibePreviewEnabled}
          dreamVibeAuthoringEnabled={dreamVibeAuthoringEnabled}
          dreamVibeRenderEnabled={dreamVibeRenderEnabled}
          sourceBlockReason={sourceErrorMessage ?? waitingMessage}
          onClose={() => setIsTimelineEditorOpen(false)}
          mediaImportBusy={isImporting}
          importedMedia={importedMedia}
          onImportMedia={(asset) => {
            renderLockLog({ event: 'reference-import', nodeId: id, source: asset.nodeId, port: asset.sourceHandle, fromIndex: asset.fromIndex })
            void handleRefSave([...currentReferences, asset])
          }}
          onOpenReferenceSelector={handleOpenReferenceSelector}
        />
      )}

      <ReferenceSelector
        isOpen={!readOnly && isRefSelectorOpen}
        onClose={() => setIsRefSelectorOpen(false)}
        onSave={handleRefSave}
        excludeNodeId={id}
        canvasNodes={allNodes}
        currentReferences={currentReferences}
        maxReferences={20}
        targetBackendType="video_editor"
        onUploadFile={uploadFile ?? undefined}
      />

      <ExportConfirmSheet
        isOpen={!readOnly && isExportSheetOpen}
        defaultName={exportSheetDefaultName}
        existingLabels={existingLabels}
        onCancel={handleCancelExport}
        onConfirm={handleConfirmExport}
      />
      {upscaleVideoFlyout}
    </div>
  )
})

VideoEditorNode.displayName = 'VideoEditorNode'
import { useNodePresentationWidth } from '../../NodePresentationContext'
